import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
  Container,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  Card,
  CardMedia,
  Input,
  IconButton,
  Divider,
  Box,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const AddProduct = () => {
  const { register, handleSubmit, reset, control } = useForm({
    defaultValues: {
      name: "",
      category: "massage_chair",
      product_type: "",
      max_benefits: "",
      rental_price: "",
      purchase_price: "",
      availability: "Available",
      general_info: [{ title: "", content: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "general_info",
  });

  const [mainImage, setMainImage] = useState(null);
  const [thumbnails, setThumbnails] = useState([]);
  const [detailMedia, setDetailMedia] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (event, newIndex) => {
    setTabIndex(newIndex);
  };

  const handleMainImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainImage({ file, url: URL.createObjectURL(file) });
    }
  };

  const handleThumbnailUpload = (e) => {
    const files = Array.from(e.target.files);
    setThumbnails((prev) => [
      ...prev,
      ...files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    ]);
  };

  const handleDetailImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setDetailMedia((prev) => [
      ...prev,
      ...files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    ]);
  };

  // submit
  const onSubmit = async (data) => {
    try {
      const formData = new FormData();

      // Append text data
      formData.append("name", data.name);
      formData.append("category", data.category);
      formData.append("product_type", data.product_type);
      formData.append("max_benefits", data.max_benefits);
      formData.append("rental_price", data.rental_price);
      formData.append("purchase_price", data.purchase_price);
      formData.append("availability", data.availability);

      // Append specification fields
      const specificationFields = [
        "product_model",
        "kneading",
        "kneading_interval",
        "rated_voltage",
        "power_consumption",
        "auto_timer",
        "remote_control",
        "airbag",
        "rolling_range",
        "massage_ball",
        "massage_modes",
        "heat_area",
        "speaker",
        "usb",
        "weight",
        "upright_dimension",
        "reclined_dimension",
        "safety_certification_no",
        "registration_no",
        "manufacturer_requestor",
        "manufacturer",
        "origin",
        "launched",
      ];

      specificationFields.forEach((field) => {
        formData.append(field, data[field] || ""); // Ensure all fields are sent
      });

      // Append main image file
      if (mainImage && mainImage.file) {
        formData.append("mainImage", mainImage.file);
      }

      // Append thumbnails
      thumbnails.forEach((thumb) => {
        formData.append("thumbnails[]", thumb.file);
      });

      // Append detail media (images/videos)
      detailMedia.forEach((media) => {
        formData.append("detailMedia[]", media.file);
      });

      // Convert general_info array into JSON string and append
      formData.append("general_info", JSON.stringify(data.general_info));

      // Send the request to the backend
      const response = await fetch(
        "https://cms-test.my-ogawa.com/backend/script/bodyfriend/product/add_product.php",
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success) {
        alert("Product added successfully!");
        reset(); // Reset form
        setMainImage(null);
        setThumbnails([]);
        setDetailMedia([]);
        setTabIndex(0); // Reset to first tab
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("An error occurred while submitting the product.");
    }
  };

  return (
    <Container sx={{ padding: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Add New Product
      </Typography>

      {/* Tabs Navigation */}
      <Tabs value={tabIndex} onChange={handleTabChange}>
        <Tab label="Basic Information" />
        <Tab label="Specifications" />
        <Tab label="Thumbnails" />
        <Tab label="Details Media" />
        <Tab label="General Information" />
      </Tabs>

      {/* Basic Information Tab */}
      {tabIndex === 0 && (
        <>
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              Basic Information
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              {/* Main Product Image Inside Basic Information */}
              <Grid item xs={12}>
                <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                  Main Product Image (Image/GIF)
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                  <Input
                    type="file"
                    accept="image/*,image/gif"
                    onChange={handleMainImageUpload}
                    sx={{ display: "none" }}
                    id="main-image-upload"
                  />
                  <label htmlFor="main-image-upload">
                    <IconButton color="primary" component="span">
                      <AddPhotoAlternateIcon fontSize="large" />
                    </IconButton>
                  </label>
                  {mainImage && (
                    <CardMedia
                      component="img"
                      height="120"
                      image={mainImage.url}
                      alt="Main Image"
                    />
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Product Name"
                  {...register("name")}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Category"
                  {...register("category")}
                  defaultValue="massage_chair"
                >
                  <MenuItem value="massage_chair">Massage Chair</MenuItem>
                  <MenuItem value="sofa">Sofa</MenuItem>
                  <MenuItem value="electronics">Electronics</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Product Type"
                  {...register("product_type")}
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Max Benefits"
                  {...register("max_benefits")}
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Rental Price"
                  {...register("rental_price")}
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Purchase Price"
                  {...register("purchase_price")}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Availability"
                  {...register("availability")}
                  defaultValue="Available"
                >
                  <MenuItem value="Available">Available</MenuItem>
                  <MenuItem value="Unavailable">Unavailable</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Card>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="contained" onClick={() => setTabIndex(1)}>
              Proceed to Specifications
            </Button>
          </Stack>
        </>
      )}

      {/* Specifications Tab */}
      {tabIndex === 1 && (
        <>
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              Product Specifications
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              {[
                "Product Model",
                "Kneading",
                "Kneading Interval",
                "Rated Voltage",
                "Power Consumption",
                "Auto Timer",
                "Remote Control",
                "Airbag",
                "Massage Ball",
                "Massage Modes",
                "Rolling Range",
                "Heat Area",
                "Speaker",
                "USB",
                "Weight",
                "Upright Dimension",
                "Recline Dimension",
                "Safety Certification No",
                "Registration No",
                "Manufacturer Requestor",
                "Manufacturer",
                "Origin",
                "Launching Date",
              ].map((label, index) => (
                <Grid item xs={6} key={index}>
                  <TextField
                    fullWidth
                    label={label}
                    {...register(label.toLowerCase().replace(/ /g, "_"))}
                  />
                </Grid>
              ))}
            </Grid>
          </Card>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="contained" onClick={() => setTabIndex(2)}>
              Proceed to Thumbnails
            </Button>
          </Stack>
        </>
      )}

      {/* Thumbnails Tab */}
      {tabIndex === 2 && (
        <>
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              Upload Thumbnails (Images Only)
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Input
              type="file"
              accept="image/*"
              multiple
              onChange={handleThumbnailUpload}
              sx={{ display: "none" }}
              id="thumb-upload"
            />
            <label htmlFor="thumb-upload">
              <IconButton color="primary" component="span">
                <AddPhotoAlternateIcon fontSize="large" />
              </IconButton>
            </label>

            {/* Thumbnails Preview */}
            <Box sx={{ display: "flex", gap: 1, mt: 2, overflowX: "auto" }}>
              {thumbnails.map((thumb, index) => (
                <Card key={index} sx={{ width: 80, height: 80 }}>
                  <CardMedia
                    component="img"
                    image={thumb.url}
                    alt={`Thumbnail ${index}`}
                  />
                </Card>
              ))}
            </Box>
          </Card>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="contained" onClick={() => setTabIndex(3)}>
              Proceed to Details Media
            </Button>
          </Stack>
        </>
      )}

      {/* Details Media Tab */}
      {tabIndex === 3 && (
        <>
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              Upload Detail Images (Images/Videos)
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleDetailImageUpload}
              sx={{ display: "none" }}
              id="detail-upload"
            />
            <label htmlFor="detail-upload">
              <IconButton color="primary" component="span">
                <AddPhotoAlternateIcon fontSize="large" />
              </IconButton>
            </label>

            {/* Detail Media Preview */}
            <Box sx={{ display: "flex", gap: 1, mt: 2, overflowX: "auto" }}>
              {detailMedia.map((media, index) => (
                <Card key={index} sx={{ width: 120, height: 120 }}>
                  {media.file.type.startsWith("image/") ? (
                    <CardMedia
                      component="img"
                      image={media.url}
                      alt={`Detail Media ${index}`}
                    />
                  ) : media.file.type.startsWith("video/") ? (
                    <video width="100%" height="100%" controls autoPlay muted>
                      <source src={media.url} type={media.file.type} />
                      Your browser does not support the video tag.
                    </video>
                  ) : null}
                </Card>
              ))}
            </Box>
          </Card>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="contained" onClick={() => setTabIndex(4)}>
              Proceed to General Information
            </Button>
          </Stack>
        </>
      )}

      {/* General Information Tab */}
      {tabIndex === 4 && (
        <>
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              General Information
            </Typography>
            <Divider sx={{ my: 2 }} />
            {fields.map((field, index) => (
              <Grid container spacing={2} key={field.id} sx={{ mb: 1 }}>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label="Title"
                    {...register(`general_info.${index}.title`)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Content"
                    {...register(`general_info.${index}.content`)}
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton color="error" onClick={() => remove(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              onClick={() => append({ title: "", content: "" })}
              startIcon={<AddIcon />}
              variant="outlined"
            >
              Add
            </Button>
          </Card>

          {/* Form Submission Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {/* <Button
              variant="outlined"
              color="secondary"
              startIcon={<SaveIcon />}
              onClick={() => reset()}
            >
              Save Draft
            </Button> */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleSubmit(onSubmit)}
            >
              Add Product
            </Button>
          </Stack>
        </>
      )}
    </Container>
  );
};

export default AddProduct;
